import React from "react";
import Modal from "react-modal";
import { Field, Form, Formik } from "formik";
import { Oval } from "react-loader-spinner";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { toggleShowAddVehicleModal, createVehicle } from "../../vehiclesSlice";
import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import { FuelType } from "../../../../models";

import "./AddVehicleModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

const validationSchema = Yup.object().shape({
  label: Yup.string().required("Label is required"),
  fuelType: Yup.string()
    .oneOf(["diesel", "gas"], "Fuel type must be diesel or gas")
    .required("Fuel type is required"),
  mpg: Yup.number()
    .transform((value, original) => (original === "" ? undefined : value))
    .typeError("MPG must be a number")
    .positive("MPG must be greater than 0")
    .max(100, "MPG must be 100 or less")
    .optional(),
});

export interface AddVehicleFormModel {
  label: string;
  fuelType: FuelType;
  // Kept as a string because the number input yields "" when empty; coerced on submit.
  mpg: string;
}

const initialValues: AddVehicleFormModel = {
  label: "",
  fuelType: "diesel",
  mpg: "",
};

export function AddVehicleModal() {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector((state) => state.vehicles.showAddVehicleModal);
  const addVehicleAPI = useAppSelector((state) => state.vehicles.createVehicle);

  React.useEffect(() => {
    if (!addVehicleAPI.loading && !!addVehicleAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (!addVehicleAPI.loading && !addVehicleAPI.error && addVehicleAPI.value) {
      toast.success("Successfully added vehicle.");
    }
  }, [
    addVehicleAPI.loading,
    addVehicleAPI.error,
    addVehicleAPI.value,
    dispatch,
  ]);

  const toggleModal = () => {
    dispatch(toggleShowAddVehicleModal());
  };

  const onSubmit = (form: AddVehicleFormModel) => {
    dispatch(
      createVehicle({
        label: form.label,
        fuelType: form.fuelType,
        mpg: form.mpg === "" ? undefined : Number(form.mpg),
      })
    );
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={toggleModal} style={modalStyles}>
      <div className="add-vehicle-modal-component">
        <Close className="close-button" onClick={toggleModal} />

        <div className="title">Add Vehicle</div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form className="add-vehicle-form">
              <div className="field-container">
                <div className="label">Label:</div>
                <Field id="label" name="label" className="text-input" />
                {errors.label && touched.label && (
                  <div className="form-error">{errors.label}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Fuel Type:</div>
                <Field
                  as="select"
                  id="fuelType"
                  name="fuelType"
                  className="text-input"
                >
                  <option value="diesel">Diesel</option>
                  <option value="gas">Gas</option>
                </Field>
                {errors.fuelType && touched.fuelType && (
                  <div className="form-error">{errors.fuelType}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">MPG:</div>
                <Field
                  id="mpg"
                  name="mpg"
                  type="number"
                  step="0.1"
                  min="0"
                  className="text-input"
                />
                {errors.mpg && touched.mpg && (
                  <div className="form-error">{errors.mpg}</div>
                )}
              </div>

              <button
                type="submit"
                className="add-button"
                disabled={addVehicleAPI.loading}
              >
                Add Vehicle
                {addVehicleAPI.loading && (
                  <div style={{ marginLeft: 8 }}>
                    <Oval
                      color="#131313"
                      height={20}
                      width={20}
                      secondaryColor="#FBD20A"
                    />
                  </div>
                )}
              </button>

              {!!addVehicleAPI.error && (
                <div className="error-text">
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
